import React from 'react';
import classnames from 'classnames';
import _ from 'lodash';

import history from 'app/routes/history';
import IActions from 'app/types/actions';
import {
  IOrganisationSectionsSelector,
  IOrganisationsSelector,
  IGroupsSelector,
  ICompetitionsSelector,
} from 'app/types/selector';
import OrganisationIcon from 'app/images/icons/organisation.svg';
import TeamIcon from 'app/images/icons/team.svg';
import SectionIcon from 'app/images/icons/rank.svg';

import SectionGroup from 'app/components/general/SectionGroup';
import InputGroupBlock from 'app/components/general/InputGroupBlock';
import Button from 'app/components/general/Button';
import validateInput from './SelectTeamValidator';

interface ISelectTeamProps {
  currentTeam: any;
  organisations: IOrganisationsSelector;
  competitions: ICompetitionsSelector;
  actions: IActions;
  handleConnect: any;
  appReady: boolean;
  groups: IGroupsSelector;
  teams: any;
  errors: any;
  organisationSections: IOrganisationSectionsSelector;
}

interface ISelectTeamState {
  changed: boolean;
  organisation: any;
  organisationName: string;
  group: any;
  groupName: string;
  section: any;
  sectionName: string;
  competition: any;
  competitionName: string;
  seasonName: string;
  errors: any;
}

export default class SelectTeam extends React.Component<
ISelectTeamProps,
ISelectTeamState
> {
  _isMounted = false;

  static defaultProps: ISelectTeamProps = {
    actions: {},
    currentTeam: {},
    organisations: {},
    competitions: {},
    groups: {},
    teams: {},
    errors: {},
    organisationSections: {},
    appReady: false,
    handleConnect: undefined,
  };

  readonly state: ISelectTeamState = {
    organisation: undefined,
    organisationName: null,
    competition: undefined,
    competitionName: null,
    group: undefined,
    groupName: null,
    section: undefined,
    sectionName: null,
    seasonName: null,
    errors: {},
  };

  componentDidMount(): void {
    if (_.isEmpty(this.props.teams?.currentTeam?.id)) {
      history.push('/teams');
    }
    this.props.actions.getAllTeams();
    if (
      this.props.actions.getAllOrganisations
      && !this.props.organisations.isLoading
      && !this.props.organisations.hasLoaded
    ) {
      this.props.actions.getAllOrganisations();
    }
    if (
      this.state.organisation !== undefined
      && this.props.actions.getAllOrganisationSections
      && !this.props.organisationSections.isLoading
    ) {
      this.props.actions.getAllOrganisationSections(
        this.state.organisation.id,
      );
    }
  }

  componentDidUpdate(prevProps, prevState): void {
    if (
      this.state.organisation !== undefined
      && this.props.actions.getAllOrganisationSections
      && !this.props.organisationSections.isLoading
      && !this.props.organisationSections.hasLoaded
    ) {
      this.props.actions.getAllOrganisationSections(
        this.state.organisation.id,
      );
    }
  }

  handleSelectTeam = (e): void => {
    this.props.actions.selectTeam(e.id);
  }

  handleOrganisationChange = (e): void => {
    delete this.state.errors.organisation;
    if (!_.isEmpty(e)) {
      this.props.actions.getAllCompetitions(e.id);
      this.setState({
        organisation: e,
        organisationName: _.isEmpty(e) ? null : e.name,
        competition: undefined,
        competitionName: null,
        group: undefined,
        groupName: null,
        section: undefined,
        sectionName: null,
      });
    }
    if (e == null) {
      this.setState({ organisation: undefined });
    }
  };

  handleCompetitionChange = (e): void => {
    delete this.state.errors.competition;
    if (!_.isEmpty(e)) {
      this.props.actions.getAllOrganisationSections(this.state.organisation.id, {
        filter: [
          {
            id: 'competition_id',
            value: e.id,
          },
          {
            id: 'context',
            value: 'section',
          },
        ],
      }, true);

      this.props.actions.getAllGroups(
        this.state.organisation.id,
        {
          filter: [
            {
              id: 'competition_id',
              value: e.id,
            },
          ],
        },
        true,
      );
      this.setState({
        competition: e,
        competitionName: e.name,
        group: undefined,
        groupName: null,
        section: undefined,
        sectionName: null,
      });
    }
    if (e == null) {
      this.setState({ competition: undefined });
    }
  };

  handleSelectSection = async (e): Promise<void> => {
    if (!_.isEmpty(e)) {
      this.setState({
        section: e,
        sectionName: _.isEmpty(e) ? null : e.name,
        group: null,
        groupName: null,
      });

      await this.props.actions.getAllGroups(
        this.state.organisation.id,
        {
          filter: [
            {
              id: 'section_tag',
              value: e.name,
            },
            {
              id: 'competition_id',
              value: this.state.competition.id,
            },
          ],
          page: {
            size: 300,
          },
        },
        true,
      );
    }
    if (e == null) {
      this.props.actions.getAllGroups(
        this.state.organisation.id,
        {
          filter: [
            {
              id: 'competition_id',
              value: this.state.competition.id,
            },
          ],
          page: {
            size: 300,
          },
        },
        true,
      );
      this.setState({ section: undefined, group: undefined });
    }
  };

  handleSelectGroup = (e): void => {
    if (!_.isEmpty(e)) {
      const section = this.props.organisationSections.organisationSectionFilteredByGroupInSelectFormat(e.id, this.listSections());
      const seasonName = this.props.groups.groupsById[e?.id]?.seasonName;

      if (!_.isEmpty(section)) {
        this.setState({
          section, sectionName: section.name, group: e, groupName: e.name, seasonName,
        });
      } else {
        this.setState({ group: e, groupName: e.name, seasonName });
      }
    }
    if (e == null) {
      this.setState({ group: undefined });
    }
  };

  handleSubmit = async (e): Promise<void> => {
    e.preventDefault();

    if (this.isValid()) {
      const payload = {
        team: {
          id: this.props.teams.currentTeam.id,
        },
        group: {
          id: this.state.group.id,
        },
        organisation: {
          id: this.state.organisation.id,
        },
        competition: {
          id: this.state.competition.id,
        },
        groupStatus: 'active',
        competitionStatus: 'active',
        teamName: this.state.groupName,
        seasonName: this.state.seasonName,
        sectionName: this.state.sectionName,
        competitionName: this.state.competitionName,
        organisationName: this.state.organisationName,
      };
      this.props.handleConnect(payload);
      history.push('/connect-team/confirm');
    }
  };

  isValid(): boolean {
    const { errors, isValid } = validateInput({
      organisationId: this.state.organisation.id,
      teamId: this.props.teams.currentTeamId,
      section: this.state.section.id,
      group: this.state.group.id,
    });

    if (!isValid) {
      this.setState({ errors });
    }

    return isValid;
  }

  listCompetitions(): any[] {
    let competitions = this.props.competitions.competitionsByIdInSelectFormat;
    if (!_.isEmpty(this.state.organisation)) {
      competitions = this.props.competitions.competitionsFilteredByOrganisationInSelectFormat(
        this.state.organisation.id,
      );
    }
    return competitions;
  }

  listSections(): any[] {
    const organisationSections = this.props.organisationSections
      .organisationSectionsByIdInSelectFormat;
    return organisationSections;
  }

  listGroups(): any[] {
    let groups = this.props.groups.groupsByIdInSelectFormat;
    const sectionId = _.isEmpty(this.state.section) ? null : this.state.section.id;
    if (!_.isEmpty(this.state.competition)) {
      groups = this.props.groups.groupsFilteredByTagAndCompetitionInSelectFormat(
        sectionId,
        this.state.competition.id,
      );
    }
    return groups;
  }

  render(): React.ReactNode {
    return (
      <div>
        <div className="row with-margin-top">
          <div className="col col-md-8 col-lg-6">
            <div className="form-description">
              Provide and confirm your team details below to connect your team
              to your association. This will enable you to manage payments,
              submit walkovers and connect with your association.
            </div>
          </div>
        </div>
        <div className="row with-margin-top-10">
          <div className="col col-md-8 col-lg-6">
            <SectionGroup title="Your team">
              <div className="row">
                <div className="col">
                  <InputGroupBlock
                    type="select"
                    icon={OrganisationIcon}
                    labelText="Select your team"
                    name="currentTeam"
                    className="height-50"
                    placeholder="Select your team"
                    aria-label="Select your team"
                    autoComplete="off"
                    tabIndex="1"
                    clearable
                    value={this.props.teams.currentTeam}
                    options={
                      this.props.teams.teamsByIdInSelectFormat
                    }
                    onChange={this.handleSelectTeam}
                  />
                </div>
              </div>
            </SectionGroup>
          </div>
        </div>
        <div className="row with-margin-top-10">
          <div className="col col-md-8 col-lg-6">
            <SectionGroup title="Association connection">
              <div className="row">
                <div className="col">
                  <InputGroupBlock
                    type="select"
                    icon={OrganisationIcon}
                    labelText="Sports organisation/league"
                    name="organisation"
                    className="height-50"
                    placeholder="Sports organisation/league"
                    aria-label="Sports organisation/league"
                    autoComplete="off"
                    tabIndex="1"
                    clearable
                    value={this.state.organisation}
                    options={
                      this.props.organisations.organisationsByIdInSelectFormat
                    }
                    onChange={this.handleOrganisationChange}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <InputGroupBlock
                    type="select"
                    icon={OrganisationIcon}
                    disabled={_.isEmpty(this.state.organisation)}
                    labelText="Competition"
                    name="competition"
                    className="height-50"
                    placeholder="Competition"
                    aria-label="Competition"
                    autoComplete="off"
                    tabIndex="2"
                    clearable
                    value={this.state.competition}
                    options={this.listCompetitions()}
                    onChange={this.handleCompetitionChange}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <InputGroupBlock
                    icon={SectionIcon}
                    disabled={
                      _.isEmpty(this.state.organisation)
                      || _.isEmpty(this.state.competition)
                    }
                    name="section"
                    type="select"
                    labelText="Section (Optional)"
                    placeholder="Section (Optional)"
                    aria-label="Section (Optional)"
                    tabIndex="3"
                    autoComplete="false"
                    clearable
                    errors={this.props.errors}
                    value={this.state.section}
                    options={this.listSections()}
                    onChange={this.handleSelectSection}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <InputGroupBlock
                    icon={TeamIcon}
                    disabled={
                      _.isEmpty(this.state.organisation)
                      || _.isEmpty(this.state.competition)
                      // || _.isEmpty(this.state.section)
                    }
                    type="select"
                    name="group"
                    labelText="Your Team"
                    placeholder="Your Team"
                    aria-label="Your Team"
                    tabIndex="4"
                    clearable
                    errors={this.props.errors}
                    value={this.state.group}
                    onChange={this.handleSelectGroup}
                    options={this.listGroups()}
                  />
                </div>
              </div>
              <div
                className={classnames('row', {
                  'd-none':
                    _.isEmpty(this.state.organisation)
                    || _.isEmpty(this.state.competition)
                    || _.isEmpty(this.state.section)
                    || _.isEmpty(this.state.group),
                })}
              >
                <div className="col btn-flex-group less-padding">
                  <div className="btn-flex-container">
                    <Button
                      className="btn-standard btn-success"
                      isLoading={this.props.teams.isLoading}
                      type="submit"
                      onClick={this.handleSubmit}
                    >
                      Select Team
                    </Button>
                  </div>
                </div>
              </div>
            </SectionGroup>
            <div className="row with-margin-top">
              <div className="col">
                <a
                  href="https://help.teammo.com.au/en/articles/3721555-i-m-trying-to-create-an-account-but-i-can-t-find-my-team-in-the-dropdown"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Can&#39;t find your team?
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
